<template>
	<div :id="uniqueUploaderId">
		<div v-if="uppyStyle === 'box'"
			:id="uniqueDragDropAreaId"
			class="uppyFileInput" />
		<input v-if="uppyStyle === 'button'"
			:id="uniqueFileInput"
			ref="uploader"
			type="file"
			:multiple="multiple"
			:required="required" />
		<b-form-file v-if="uppyStyle === 'form'"
			:id="uniqueFileInput"
			ref="uploader"
			:multiple="multiple"
			:required="required" />

		<div v-if="errorMessage" class="text-danger">
			{{ errorMessage }}
		</div>
	</div>
</template>

<script>
import {mapState} from 'vuex'
import api from '@/lib/api'
import auth from '@/Mixin/auth'

import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import XHRUpload from '@uppy/xhr-upload'
import AwsS3 from '@uppy/aws-s3'
import Webcam from '@uppy/webcam'
import DropTarget from '@uppy/drop-target'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import '@uppy/webcam/dist/style.min.css'

export default {
	name: 'UppyUploader',
	mixins: [ auth ],
	props: {
		uppyStyle: {
			type: String,
			default: 'box'
		},
		name: {
			type: String,
			default: 'UppyUploader'
		},
		fileDisk: {
			type: String,
			default: 'local'
		},
		overrideUrl: {
			type: String,
			default: null
		},
		inline: {
			type: Boolean,
			default: true
		},
		width: {
			type: String,
			default: '100%' // '100%' works here
		},
		height: {
			type: String,
			default: '335px'
		},
		theme: {
			type: String,
			default: 'dark'
		},
		value: {
			type: String,
			default: null
		},
		help: {
			type: String,
			default: null
		},
		webcam: {
			type: Boolean,
			default: false
		},
		private: {
			type: Boolean,
			default: false
		},
		modelType: {
			type: String,
			default: null
		},
		modelId: {
			type: Number,
			default: null
		},
		path: {
			type: String,
			default: 'uppy-temp'
		},
		type: {
			type: String,
			default: 'document'
		},
		meta: {
			type: Array,
			default () { return [] }
		},
		maxNumberOfFiles: {
			type: Number,
			default: null
		},
		minNumberOfFiles: {
			type: Number,
			default: null
		},
		maxFileSize: {
			type: Number,
			default: null
		},
		allowedFileTypes: {
			type: String,
			default: null
		},
		enableAutoProceed: {
			type: Boolean,
			default: false
		},
		tempUpload: {
			type: Boolean,
			default: false
		},
		multiple: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		uploading: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			userId: null,
			uniqueUploaderId: 'uppy-uploader-' + this.generateRandomString(10),
			uniqueDragDropAreaId: 'uppy-drag-and-drop-' + this.generateRandomString(10),
			uniqueFileInput: 'o2-file-input-' + this.generateRandomString(10),
			errorMessage: '',
			autoProceed: false,
			file_disk: null,
			file_disk_name: null
		}
	},

	computed: {
		...mapState({
			disks: state => state.init.disks,
			initialized: state => state.init.initialized,
			app_url: state => state.init.system.url
		})
	},

	watch: {
		initialized (newValue) {
			if (newValue) {
				this.initUppy()
			}
		}
	},

	mounted () {
		if (this.initialized) {
			this.initUppy()
		}
	},

	methods: {
		initUppy () {
			this.userId = this.auth.id
			this.autoProceed = this.enableAutoProceed

			if (this.tempUpload) {
				this.autoProceed = true
			} else {
				let key = this.fileDisk.toLowerCase()
				if (this.disks.hasOwnProperty(key)) {
					this.file_disk = this.disks[key].disk
					this.file_disk_name = this.disks[key].name
				}
			}

			const restrictions = {
				maxNumberOfFiles: this.maxNumberOfFiles,
				minNumberOfFiles: this.minNumberOfFiles,
				maxFileSize: this.maxFileSize,
				allowedFileTypes: this.allowedFileTypes ? this.allowedFileTypes.split(',') : null
			}

			// create the uppy object
			this.uppy = new Uppy({
				id: this.uniqueUploaderId,
				autoProceed: this.autoProceed,
				debug: false,
				restrictions,
				onBeforeFileAdded: (file) => {
					return this.uppyStyle === 'box' ? this.isVisible() : true
				}
			})

			// set up how the component looks
			if (this.uppyStyle === 'button' || this.uppyStyle === 'form') {
				this.attachCustomFileInput()
			} else {
				// default Uppy dashboard config
				this.uppy.use(Dashboard, {
					target: `#${this.uniqueDragDropAreaId}`,
					theme: this.theme,
					width: this.width,
					height: this.height,
					inline: this.inline,
					note: this.help,
					proudlyDisplayPoweredByUppy: false // get rekt
				})

				this.uppy.use(DropTarget, {
					target: document.body,
					onDragOver: (event) => {
						this.$emit('file-hover')
					},
					onDrop: (event) => {
						this.$emit('file-dropped')
					}
				})
			}

			// set up how uppy uploads files
			if (this.tempUpload) {
				if (this.disks.uppy_temp === 'local') {
					this.useLocalEndpoint(route('file.temp-upload'))
				} else {
					this.useS3Endpoint('uppy-temp')
				}
			} else if (this.file_disk === 'local') {
				this.useLocalEndpoint(this.overrideUrl ?? route('file.create'))

				// attach additional file details to build the file model when using the default file create endpoint
				if (this.overrideUrl === null) {
					this.uppy.on('file-added', (file) => {
						this.uppy.setMeta({
							model_type: this.modelType,
							model_id: this.modelId,
							user_id: this.userId,
							path: this.path,
							private: this.private,
							disk: this.file_disk_name,
							type: this.type
						})
					})
				}
			} else {
				this.useS3Endpoint(this.path)
			}

			if (this.webcam) {
				this.uppy.use(Webcam)
			}

			// set up what happens when uppy.upload() finishes
			this.uppy.on('complete', async (result) => {
				// use the api to generate the File model for this s3 upload
				if (this.file_disk === 's3' && ! this.tempUpload) {
					if (result.successful) {
						// build api call
						const url = route('file.store')
						const method = 'POST'
						const data = {
							model_type: this.modelType,	// model to attach the files to
							model_id: this.modelId,
							user_id: this.userId,	// user id for file created/updated by
							disk: this.file_disk_name,
							files: result.successful.map(file => ({
								private: this.private,	// file model attributes
								name: file.name,
								path: file.uploadURL.split('.com/')[1], // confirmed upload path from uppy
								mime: file.type,
								type: this.type,
								size: file.size,
								meta: this.meta
							}))
						}

						await api({
							method,
							url,
							data
						}).then(response => {
							this.uppy.clear()
							this.$emit('file-upload-complete', result.successful)
						})
					} else {
						console.error(result.failed)
					}
				} else {
					// temp upload success, set uploadURL with laravel api path
					result.successful.forEach((file, index) => {
						if (!file.uploadURL) {
							const filePath = file.response?.body?.files?.[index]
							if (filePath) {
								file.uploadURL = filePath
							}
						}
					})

					this.$nextTick(() => {
						this.uppy.clear()
						this.$emit('file-upload-complete', result.successful)
					})
				}
			})

			this.uppy.on('beforeFileAdded', (currentFile, files) => {
				if (! this.isVisible()) {
					return false
				}

				return true
			})
		},

		attachCustomFileInput () {
			// bind Uppy to our file input
			const fileInput = document.querySelector(`#${this.uniqueFileInput}`)

			fileInput.addEventListener('change', (event) => {
				const files = Array.from(event.target.files)
				this.errorMessage = ''

				files.forEach((file) => {
					try {
						this.uppy.addFile({
							source: 'file input',
							name: file.name,
							type: file.type,
							data: file
						})
					} catch (err) {
						this.errorMessage = err
						console.error(err)

						fileInput.value = null
						this.clearFiles()
					}
				})
			})

			this.uppy.on('file-removed', () => {
				fileInput.value = null
			})

			this.uppy.on('upload-start', () => {
				this.handleUploadStart()
			})

			this.uppy.on('complete', (result) => {
				if (! this.autoProceed) {
					fileInput.value = null
				}
			})
		},

		useLocalEndpoint (url) {
			this.uppy.use(XHRUpload, {
				endpoint: url,
				bundle: true,
				formData: true,
				headers: {
					'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
				}
			})
		},

		useS3Endpoint (path) {
			const self = this
			this.uppy.use(AwsS3, {
				// aws needs a signed url to upload to from custom getUploadParameters implementation
				async getUploadParameters (file) {
					const uploadPath = `${path.replace(/^\/+|\/+$/g, '')}/${self.generateRandomString(40)}.${file.name.split('.').pop()}`
					const url = route('s3.sign')
					const method = 'POST'
					const data = {
						headers: {
							'Content-Type': 'application/json',
							'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
						},
						body: JSON.stringify({
							filename: uploadPath,
							filetype: file.type
						})
					}

					const response = await api({
						method,
						url,
						data
					})

					return {
						method: 'PUT',
						url: response.data.url,
						fields: [],
						headers: {
							'Content-Type': file.type,
							'Access-Control-Allow-Origin': self.app_url
						}
					}
				}
			})
		},

		handleUploadStart () {
			this.$emit('upload-start')
		},

		clearFiles () {
			this.$refs.uploader.reset()
			this.uppy.clear()
		},

		generateRandomString (length) {
			const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890'
			let result = ''
			for (let i = 0; i < length; i++) {
				result += characters.charAt(Math.floor(Math.random() * characters.length))
			}
			return result
		},

		isVisible () {
			const targetElement = document.querySelector(`#${this.uniqueUploaderId}`)
			return targetElement && targetElement.offsetParent !== null
		}
	}
}
</script>

<style scoped>
#drag-drop-area {
	width: 100%;
	height: 100%;
}
</style>
